// src/finance/FinancialRealityModal.tsx
//
// BekalNanti — Milestone P2 Financial Reality Workspace Modal
// Structured, non-judgmental mapping of:
// 1. Incomes (Pemasukan)
// 2. Expenses (Pengeluaran Pokok & Diskresioner)
// 3. Debts & Obligations (Cicilan & Komitmen Rutin)
// 4. Savings (Tabungan & Dana Bekal Awal)
// 5. Summary (Ringkasan Realita & Kapasitas Bersih)

import React, { useState } from 'react'
import {
  X,
  Plus,
  Trash2,
  DollarSign,
  TrendingUp,
  Receipt,
  CreditCard,
  PiggyBank,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  Sparkles,
  ArrowRight,
  ArrowLeft,
} from 'lucide-react'
import {
  FinancialRealityData,
  IncomeRecord,
  ExpenseRecord,
  DebtRecord,
  SavingRecord,
  ObligationRecord,
} from '../journey/types'
import {
  formatIDR,
  formatCompactIDR,
  summarizeFinancialReality,
  toMonthlyAmount,
} from '../lib/financialCalculation'
import {
  addIncome,
  deleteIncome,
  addExpense,
  deleteExpense,
  addDebt,
  deleteDebt,
  addSaving,
  deleteSaving,
  addObligation,
  deleteObligation,
} from '../lib/financeService'

interface FinancialRealityModalProps {
  isOpen: boolean
  householdId: string
  initialData?: FinancialRealityData
  onClose: () => void
  onRefreshJourney: () => Promise<void>
}

type TabType = 'income' | 'expense' | 'commitment' | 'saving' | 'summary'

export const FinancialRealityModal: React.FC<FinancialRealityModalProps> = ({
  isOpen,
  householdId,
  initialData,
  onClose,
  onRefreshJourney,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('income')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [feedbackMsg, setFeedbackMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Local state for interactive editing
  const [incomes, setIncomes] = useState<IncomeRecord[]>(initialData?.incomes || [])
  const [expenses, setExpenses] = useState<ExpenseRecord[]>(initialData?.expenses || [])
  const [debts, setDebts] = useState<DebtRecord[]>(initialData?.debts || [])
  const [obligations, setObligations] = useState<ObligationRecord[]>(initialData?.obligations || [])
  const [savings, setSavings] = useState<SavingRecord[]>(initialData?.savings || [])

  // Sync with initialData whenever it updates from onRefreshJourney
  React.useEffect(() => {
    if (initialData) {
      setIncomes(initialData.incomes || [])
      setExpenses(initialData.expenses || [])
      setDebts(initialData.debts || [])
      setObligations(initialData.obligations || [])
      setSavings(initialData.savings || [])
    }
  }, [initialData])

  // Form states for adding items
  const [newIncomeName, setNewIncomeName] = useState('')
  const [newIncomeAmount, setNewIncomeAmount] = useState('')
  const [newIncomeFreq, setNewIncomeFreq] = useState<'monthly' | 'weekly' | 'annual'>('monthly')

  const [newExpenseName, setNewExpenseName] = useState('')
  const [newExpenseAmount, setNewExpenseAmount] = useState('')
  const [newExpenseFreq, setNewExpenseFreq] = useState<'monthly' | 'annual'>('monthly')
  const [newExpenseEssential, setNewExpenseEssential] = useState(true)

  const [newDebtName, setNewDebtName] = useState('')
  const [newDebtOutstanding, setNewDebtOutstanding] = useState('')
  const [newDebtMonthly, setNewDebtMonthly] = useState('')
  const [newDebtMonths, setNewDebtMonths] = useState('')

  const [newObligationName, setNewObligationName] = useState('')
  const [newObligationAmount, setNewObligationAmount] = useState('')
  const [newObligationFreq, setNewObligationFreq] = useState<'monthly' | 'annual'>('monthly')

  const [newSavingName, setNewSavingName] = useState('')
  const [newSavingBalance, setNewSavingBalance] = useState('')
  const [newSavingPurpose, setNewSavingPurpose] = useState('education')
  const [newSavingAvailable, setNewSavingAvailable] = useState(true)

  if (!isOpen) return null

  // Calculate live summary
  const liveSummary = summarizeFinancialReality({
    incomes,
    expenses,
    debts,
    savings,
    obligations,
  })

  // Quick Preset Actions for fast onboarding
  const handleApplyPreset = async () => {
    try {
      setIsSubmitting(true)
      setFeedbackMsg(null)
      // Add typical Indonesian family baseline
      const inc = await addIncome(householdId, {
        name: 'Penghasilan Utama',
        amount: 15_000_000,
        frequency: 'monthly',
        sourceType: 'salary',
      })
      const exp1 = await addExpense(householdId, {
        name: 'Kebutuhan Pokok & Dapur',
        amount: 6_000_000,
        frequency: 'monthly',
        isEssential: true,
      })
      const exp2 = await addExpense(householdId, {
        name: 'Utilitas & Tempat Tinggal',
        amount: 2_500_000,
        frequency: 'monthly',
        isEssential: true,
      })
      const sav = await addSaving(householdId, {
        name: 'Tabungan Saat Ini',
        balance: 20_000_000,
        purpose: 'education',
        isAvailableForGoal: true,
      })
      setIncomes((prev) => [...prev, inc])
      setExpenses((prev) => [...prev, exp1, exp2])
      setSavings((prev) => [...prev, sav])
      await onRefreshJourney()
      setFeedbackMsg({ type: 'success', text: 'Data contoh berhasil diterapkan sebagai titik awal!' })
      setActiveTab('summary')
    } catch (err: any) {
      setFeedbackMsg({ type: 'error', text: err.message || 'Gagal menerapkan preset.' })
    } finally {
      setIsSubmitting(false)
    }
  }

  // --- Handlers for Incomes ---
  const handleAddIncome = async (e: React.FormEvent) => {
    e.preventDefault()
    const amount = Number(newIncomeAmount.replace(/[^0-9]/g, ''))
    if (!newIncomeName.trim() || amount <= 0) return

    try {
      setIsSubmitting(true)
      const created = await addIncome(householdId, {
        name: newIncomeName.trim(),
        amount,
        frequency: newIncomeFreq,
      })
      if (created) setIncomes((prev) => [...prev, created])
      setNewIncomeName('')
      setNewIncomeAmount('')
      await onRefreshJourney()
      setFeedbackMsg({ type: 'success', text: 'Penghasilan berhasil dicatat!' })
    } catch (err: any) {
      setFeedbackMsg({ type: 'error', text: err.message })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteIncome = async (id: string) => {
    try {
      setIsSubmitting(true)
      await deleteIncome(id)
      setIncomes((prev) => prev.filter((i) => i.id !== id))
      await onRefreshJourney()
    } catch (err: any) {
      setFeedbackMsg({ type: 'error', text: err.message })
    } finally {
      setIsSubmitting(false)
    }
  }

  // --- Handlers for Expenses ---
  const handleAddExpense = async (e: React.FormEvent) => {
    e.preventDefault()
    const amount = Number(newExpenseAmount.replace(/[^0-9]/g, ''))
    if (!newExpenseName.trim() || amount <= 0) return

    try {
      setIsSubmitting(true)
      const created = await addExpense(householdId, {
        name: newExpenseName.trim(),
        amount,
        frequency: newExpenseFreq,
        isEssential: newExpenseEssential,
      })
      if (created) setExpenses((prev) => [...prev, created])
      setNewExpenseName('')
      setNewExpenseAmount('')
      await onRefreshJourney()
      setFeedbackMsg({ type: 'success', text: 'Pengeluaran berhasil dicatat!' })
    } catch (err: any) {
      setFeedbackMsg({ type: 'error', text: err.message })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteExpense = async (id: string) => {
    try {
      setIsSubmitting(true)
      await deleteExpense(id)
      setExpenses((prev) => prev.filter((e) => e.id !== id))
      await onRefreshJourney()
    } catch (err: any) {
      setFeedbackMsg({ type: 'error', text: err.message })
    } finally {
      setIsSubmitting(false)
    }
  }

  // --- Handlers for Debts ---
  const handleAddDebt = async (e: React.FormEvent) => {
    e.preventDefault()
    const monthly = Number(newDebtMonthly.replace(/[^0-9]/g, ''))
    const outstanding = Number(newDebtOutstanding.replace(/[^0-9]/g, '')) || 0
    if (!newDebtName.trim() || monthly <= 0) return

    try {
      setIsSubmitting(true)
      const created = await addDebt(householdId, {
        name: newDebtName.trim(),
        monthlyPayment: monthly,
        outstandingAmount: outstanding,
        remainingMonths: newDebtMonths ? Number(newDebtMonths) : null,
      })
      if (created) setDebts((prev) => [...prev, created])
      setNewDebtName('')
      setNewDebtMonthly('')
      setNewDebtOutstanding('')
      setNewDebtMonths('')
      await onRefreshJourney()
      setFeedbackMsg({ type: 'success', text: 'Cicilan berhasil dicatat!' })
    } catch (err: any) {
      setFeedbackMsg({ type: 'error', text: err.message })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteDebt = async (id: string) => {
    try {
      setIsSubmitting(true)
      await deleteDebt(id)
      setDebts((prev) => prev.filter((d) => d.id !== id))
      await onRefreshJourney()
    } catch (err: any) {
      setFeedbackMsg({ type: 'error', text: err.message })
    } finally {
      setIsSubmitting(false)
    }
  }

  // --- Handlers for Obligations ---
  const handleAddObligation = async (e: React.FormEvent) => {
    e.preventDefault()
    const amount = Number(newObligationAmount.replace(/[^0-9]/g, ''))
    if (!newObligationName.trim() || amount <= 0) return

    try {
      setIsSubmitting(true)
      const created = await addObligation(householdId, {
        name: newObligationName.trim(),
        amount,
        frequency: newObligationFreq,
      })
      if (created) setObligations((prev) => [...prev, created])
      setNewObligationName('')
      setNewObligationAmount('')
      await onRefreshJourney()
      setFeedbackMsg({ type: 'success', text: 'Kewajiban rutin berhasil dicatat!' })
    } catch (err: any) {
      setFeedbackMsg({ type: 'error', text: err.message })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteObligation = async (id: string) => {
    try {
      setIsSubmitting(true)
      await deleteObligation(id)
      setObligations((prev) => prev.filter((o) => o.id !== id))
      await onRefreshJourney()
    } catch (err: any) {
      setFeedbackMsg({ type: 'error', text: err.message })
    } finally {
      setIsSubmitting(false)
    }
  }

  // --- Handlers for Savings ---
  const handleAddSaving = async (e: React.FormEvent) => {
    e.preventDefault()
    const balance = Number(newSavingBalance.replace(/[^0-9]/g, ''))
    if (!newSavingName.trim() || balance < 0) return

    try {
      setIsSubmitting(true)
      const created = await addSaving(householdId, {
        name: newSavingName.trim(),
        balance,
        purpose: newSavingPurpose,
        isAvailableForGoal: newSavingAvailable,
      })
      if (created) setSavings((prev) => [...prev, created])
      setNewSavingName('')
      setNewSavingBalance('')
      await onRefreshJourney()
      setFeedbackMsg({ type: 'success', text: 'Tabungan/Aset berhasil dicatat!' })
    } catch (err: any) {
      setFeedbackMsg({ type: 'error', text: err.message })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteSaving = async (id: string) => {
    try {
      setIsSubmitting(true)
      await deleteSaving(id)
      setSavings((prev) => prev.filter((s) => s.id !== id))
      await onRefreshJourney()
    } catch (err: any) {
      setFeedbackMsg({ type: 'error', text: err.message })
    } finally {
      setIsSubmitting(false)
    }
  }

  const tabs = [
    { id: 'income', label: '1. Pemasukan', icon: TrendingUp, count: incomes.length },
    { id: 'expense', label: '2. Pengeluaran', icon: Receipt, count: expenses.length },
    { id: 'commitment', label: '3. Cicilan/Kewajiban', icon: CreditCard, count: debts.length + obligations.length },
    { id: 'saving', label: '4. Tabungan & Bekal', icon: PiggyBank, count: savings.length },
    { id: 'summary', label: '5. Ringkasan Realita', icon: CheckCircle2, count: null },
  ]

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/65 backdrop-blur-xs animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="financial-reality-title"
    >
      <div className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[92vh]">
        {/* Modal Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-teal-50 text-[#0EA5A4] flex items-center justify-center shadow-2xs">
              <DollarSign size={20} />
            </div>
            <div>
              <h2 id="financial-reality-title" className="text-base font-bold text-slate-900 leading-tight">
                Realita Finansial Keluarga
              </h2>
              <p className="text-[11px] text-slate-500">
                Peta arus kas dan kesiapan bekal tanpa asumsi kosong
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 transition-colors cursor-pointer"
            aria-label="Tutup"
          >
            <X size={18} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-slate-200/80 bg-slate-100/60 px-3 pt-2 gap-1 overflow-x-auto no-scrollbar">
          {tabs.map((tab) => {
            const Icon = tab.icon
            const isActive = activeTab === tab.id
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setActiveTab(tab.id as TabType)
                  setFeedbackMsg(null)
                }}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-t-xl transition-all whitespace-nowrap cursor-pointer ${
                  isActive
                    ? 'bg-white text-[#0F766E] border-t-2 border-[#0EA5A4] shadow-2xs'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-white/40'
                }`}
              >
                <Icon size={14} className={isActive ? 'text-[#0EA5A4]' : 'text-slate-400'} />
                <span>{tab.label}</span>
                {tab.count !== null && tab.count > 0 && (
                  <span className="ml-0.5 px-1.5 py-0.2 rounded-full text-[10px] bg-teal-100 text-teal-800 font-bold">
                    {tab.count}
                  </span>
                )}
              </button>
            )
          })}
        </div>

        {/* Feedback Message */}
        {feedbackMsg && (
          <div
            className={`px-4 py-2 text-xs flex items-center gap-2 ${
              feedbackMsg.type === 'success'
                ? 'bg-emerald-50 text-emerald-800 border-b border-emerald-100'
                : 'bg-rose-50 text-rose-800 border-b border-rose-100'
            }`}
          >
            {feedbackMsg.type === 'success' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
            <span>{feedbackMsg.text}</span>
          </div>
        )}

        {/* Scrollable Content Body */}
        <div className="p-4 sm:p-5 overflow-y-auto space-y-4 text-left text-xs text-slate-700 flex-1">
          {/* TAB 1: PEMASUKAN */}
          {activeTab === 'income' && (
            <div className="space-y-4">
              <div className="p-3 rounded-2xl bg-teal-50/70 border border-teal-100 flex items-start gap-2.5">
                <HelpCircle size={16} className="text-[#0EA5A4] flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-teal-900 leading-relaxed">
                  Masukkan estimasi penghasilan rutin keluarga (gaji pokok, usaha rutin, atau penghasilan bulanan lainnya). Angka tidak harus sempurna sampai rupiah terkecil.
                </p>
              </div>

              {/* Add Income Form */}
              <form onSubmit={handleAddIncome} className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-3">
                <h4 className="font-bold text-slate-900 text-xs flex items-center gap-1.5">
                  <Plus size={14} className="text-[#0EA5A4]" />
                  <span>Tambah Sumber Pemasukan</span>
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="sm:col-span-1">
                    <label className="block text-[10px] font-semibold text-slate-500 mb-1">
                      Nama Pemasukan
                    </label>
                    <input
                      type="text"
                      placeholder="Contoh: Gaji Pokok"
                      value={newIncomeName}
                      onChange={(e) => setNewIncomeName(e.target.value)}
                      className="w-full px-3 py-1.5 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-1 focus:ring-teal-500 bg-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-500 mb-1">
                      Nominal (Rp)
                    </label>
                    <input
                      type="number"
                      placeholder="15000000"
                      value={newIncomeAmount}
                      onChange={(e) => setNewIncomeAmount(e.target.value)}
                      className="w-full px-3 py-1.5 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-1 focus:ring-teal-500 bg-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-500 mb-1">
                      Frekuensi
                    </label>
                    <div className="flex gap-1">
                      <select
                        value={newIncomeFreq}
                        onChange={(e) => setNewIncomeFreq(e.target.value as any)}
                        className="w-full px-2 py-1.5 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-1 focus:ring-teal-500 bg-white"
                      >
                        <option value="monthly">Bulanan</option>
                        <option value="weekly">Mingguan</option>
                        <option value="annual">Tahunan</option>
                      </select>
                      <button
                        type="submit"
                        disabled={isSubmitting || !newIncomeName.trim() || !newIncomeAmount}
                        className="px-3 py-1.5 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white font-bold text-xs shadow-2xs transition-all disabled:opacity-50 cursor-pointer flex-shrink-0"
                      >
                        Simpan
                      </button>
                    </div>
                  </div>
                </div>
              </form>

              {/* Income List */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-slate-500 font-semibold text-[11px]">
                  <span>Daftar Pemasukan Terpetakan ({incomes.length})</span>
                  <span>Total Bulanan: {formatIDR(liveSummary.totalMonthlyIncome)}</span>
                </div>

                {incomes.length === 0 ? (
                  <div className="p-4 rounded-2xl border border-dashed border-slate-200 text-center text-slate-400">
                    <p>Belum ada sumber penghasilan yang dicatat.</p>
                    <button
                      type="button"
                      onClick={handleApplyPreset}
                      disabled={isSubmitting}
                      className="mt-2 text-xs font-bold text-[#0EA5A4] hover:underline cursor-pointer inline-flex items-center gap-1"
                    >
                      <Sparkles size={13} />
                      <span>Gunakan Contoh Baseline Keluarga (Rp 15 Jt/bln)</span>
                    </button>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {incomes.map((inc) => (
                      <div
                        key={inc.id}
                        className="p-2.5 rounded-xl bg-white border border-slate-200 flex items-center justify-between gap-2 shadow-2xs"
                      >
                        <div>
                          <p className="font-bold text-slate-900 text-xs">{inc.name}</p>
                          <p className="text-[10px] text-slate-400 capitalize">
                            {formatIDR(inc.amount)} / {inc.frequency === 'monthly' ? 'bulan' : inc.frequency === 'weekly' ? 'minggu' : 'tahun'}
                            {inc.frequency !== 'monthly' && ` (setara ${formatIDR(toMonthlyAmount(inc.amount, inc.frequency))}/bln)`}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeleteIncome(inc.id)}
                          disabled={isSubmitting}
                          className="p-1 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors cursor-pointer"
                          aria-label="Hapus pemasukan"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 2: PENGELUARAN */}
          {activeTab === 'expense' && (
            <div className="space-y-4">
              <div className="p-3 rounded-2xl bg-teal-50/70 border border-teal-100 flex items-start gap-2.5">
                <HelpCircle size={16} className="text-[#0EA5A4] flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-teal-900 leading-relaxed">
                  Bagi pengeluaran menjadi <strong>Esensial (kebutuhan mutlak bertahan hidup)</strong> dan <strong>Diskresioner (gaya hidup/opsional)</strong>. Ini krusial agar simulasi bekal tahu batas aman keluarga.
                </p>
              </div>

              {/* Add Expense Form */}
              <form onSubmit={handleAddExpense} className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-3">
                <h4 className="font-bold text-slate-900 text-xs flex items-center gap-1.5">
                  <Plus size={14} className="text-[#0EA5A4]" />
                  <span>Tambah Pengeluaran</span>
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                  <div className="sm:col-span-1">
                    <label className="block text-[10px] font-semibold text-slate-500 mb-1">
                      Kebutuhan / Pos
                    </label>
                    <input
                      type="text"
                      placeholder="Contoh: Belanja Dapur"
                      value={newExpenseName}
                      onChange={(e) => setNewExpenseName(e.target.value)}
                      className="w-full px-3 py-1.5 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-1 focus:ring-teal-500 bg-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-500 mb-1">
                      Nominal (Rp)
                    </label>
                    <input
                      type="number"
                      placeholder="5000000"
                      value={newExpenseAmount}
                      onChange={(e) => setNewExpenseAmount(e.target.value)}
                      className="w-full px-3 py-1.5 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-1 focus:ring-teal-500 bg-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-500 mb-1">
                      Kategori Sifat
                    </label>
                    <select
                      value={newExpenseEssential ? 'true' : 'false'}
                      onChange={(e) => setNewExpenseEssential(e.target.value === 'true')}
                      className="w-full px-2 py-1.5 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-1 focus:ring-teal-500 bg-white"
                    >
                      <option value="true">Esensial (Wajib)</option>
                      <option value="false">Diskresioner (Opsional)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-500 mb-1">
                      Frekuensi
                    </label>
                    <div className="flex gap-1">
                      <select
                        value={newExpenseFreq}
                        onChange={(e) => setNewExpenseFreq(e.target.value as any)}
                        className="w-full px-2 py-1.5 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-1 focus:ring-teal-500 bg-white"
                      >
                        <option value="monthly">Bulanan</option>
                        <option value="annual">Tahunan</option>
                      </select>
                      <button
                        type="submit"
                        disabled={isSubmitting || !newExpenseName.trim() || !newExpenseAmount}
                        className="px-3 py-1.5 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white font-bold text-xs shadow-2xs transition-all disabled:opacity-50 cursor-pointer flex-shrink-0"
                      >
                        Simpan
                      </button>
                    </div>
                  </div>
                </div>
              </form>

              {/* Expense List */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-slate-500 font-semibold text-[11px]">
                  <span>Daftar Pengeluaran ({expenses.length})</span>
                  <span>Total Bulanan: {formatIDR(liveSummary.totalMonthlyExpenses)}</span>
                </div>

                {expenses.length === 0 ? (
                  <div className="p-4 rounded-2xl border border-dashed border-slate-200 text-center text-slate-400">
                    <p>Belum ada pos pengeluaran yang dicatat.</p>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {expenses.map((exp) => (
                      <div
                        key={exp.id}
                        className="p-2.5 rounded-xl bg-white border border-slate-200 flex items-center justify-between gap-2 shadow-2xs"
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                              exp.isEssential
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-slate-100 text-slate-600'
                            }`}
                          >
                            {exp.isEssential ? 'Esensial' : 'Opsional'}
                          </span>
                          <div>
                            <p className="font-bold text-slate-900 text-xs">{exp.name}</p>
                            <p className="text-[10px] text-slate-400 capitalize">
                              {formatIDR(exp.amount)} / {exp.frequency === 'monthly' ? 'bulan' : 'tahun'}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeleteExpense(exp.id)}
                          disabled={isSubmitting}
                          className="p-1 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors cursor-pointer"
                          aria-label="Hapus pengeluaran"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 3: CICILAN & KEWAJIBAN */}
          {activeTab === 'commitment' && (
            <div className="space-y-4">
              <div className="p-3 rounded-2xl bg-teal-50/70 border border-teal-100 flex items-start gap-2.5">
                <HelpCircle size={16} className="text-[#0EA5A4] flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-teal-900 leading-relaxed">
                  Catat cicilan berjalan (KPR, kendaraan) dan kewajiban rutin (misal bantuan orang tua / generasi sandwich). Komitmen ini mengurangi kapasitas tabungan bulanan.
                </p>
              </div>

              {/* Add Debt Form */}
              <form onSubmit={handleAddDebt} className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-3">
                <h4 className="font-bold text-slate-900 text-xs flex items-center gap-1.5">
                  <CreditCard size={14} className="text-[#0EA5A4]" />
                  <span>Tambah Cicilan / Hutang Aktif</span>
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                  <div className="sm:col-span-1">
                    <label className="block text-[10px] font-semibold text-slate-500 mb-1">
                      Nama Cicilan
                    </label>
                    <input
                      type="text"
                      placeholder="Contoh: KPR Rumah"
                      value={newDebtName}
                      onChange={(e) => setNewDebtName(e.target.value)}
                      className="w-full px-3 py-1.5 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-1 focus:ring-teal-500 bg-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-500 mb-1">
                      Bayar / Bulan (Rp)
                    </label>
                    <input
                      type="number"
                      placeholder="3500000"
                      value={newDebtMonthly}
                      onChange={(e) => setNewDebtMonthly(e.target.value)}
                      className="w-full px-3 py-1.5 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-1 focus:ring-teal-500 bg-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-500 mb-1">
                      Sisa Pokok (Opsional)
                    </label>
                    <input
                      type="number"
                      placeholder="200000000"
                      value={newDebtOutstanding}
                      onChange={(e) => setNewDebtOutstanding(e.target.value)}
                      className="w-full px-3 py-1.5 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-1 focus:ring-teal-500 bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-500 mb-1">
                      Sisa Tenor (Bulan)
                    </label>
                    <div className="flex gap-1">
                      <input
                        type="number"
                        placeholder="60"
                        value={newDebtMonths}
                        onChange={(e) => setNewDebtMonths(e.target.value)}
                        className="w-full px-3 py-1.5 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-1 focus:ring-teal-500 bg-white"
                      />
                      <button
                        type="submit"
                        disabled={isSubmitting || !newDebtName.trim() || !newDebtMonthly}
                        className="px-3 py-1.5 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white font-bold text-xs shadow-2xs transition-all disabled:opacity-50 cursor-pointer flex-shrink-0"
                      >
                        Simpan
                      </button>
                    </div>
                  </div>
                </div>
              </form>

              {/* Add Obligation Form */}
              <form onSubmit={handleAddObligation} className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-3">
                <h4 className="font-bold text-slate-900 text-xs flex items-center gap-1.5">
                  <Plus size={14} className="text-[#0EA5A4]" />
                  <span>Tambah Kewajiban Rutin Keluarga</span>
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="sm:col-span-1">
                    <label className="block text-[10px] font-semibold text-slate-500 mb-1">
                      Nama Kewajiban
                    </label>
                    <input
                      type="text"
                      placeholder="Contoh: Bantuan Ortu"
                      value={newObligationName}
                      onChange={(e) => setNewObligationName(e.target.value)}
                      className="w-full px-3 py-1.5 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-1 focus:ring-teal-500 bg-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-500 mb-1">
                      Nominal (Rp)
                    </label>
                    <input
                      type="number"
                      placeholder="1500000"
                      value={newObligationAmount}
                      onChange={(e) => setNewObligationAmount(e.target.value)}
                      className="w-full px-3 py-1.5 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-1 focus:ring-teal-500 bg-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-500 mb-1">
                      Frekuensi
                    </label>
                    <div className="flex gap-1">
                      <select
                        value={newObligationFreq}
                        onChange={(e) => setNewObligationFreq(e.target.value as any)}
                        className="w-full px-2 py-1.5 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-1 focus:ring-teal-500 bg-white"
                      >
                        <option value="monthly">Bulanan</option>
                        <option value="annual">Tahunan</option>
                      </select>
                      <button
                        type="submit"
                        disabled={isSubmitting || !newObligationName.trim() || !newObligationAmount}
                        className="px-3 py-1.5 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white font-bold text-xs shadow-2xs transition-all disabled:opacity-50 cursor-pointer flex-shrink-0"
                      >
                        Simpan
                      </button>
                    </div>
                  </div>
                </div>
              </form>

              {/* Debts & Obligations List */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-slate-500 font-semibold text-[11px]">
                  <span>Total Komitmen Bulanan</span>
                  <span>{formatIDR(liveSummary.totalMonthlyCommitments)}</span>
                </div>

                {debts.length === 0 && obligations.length === 0 ? (
                  <div className="p-4 rounded-2xl border border-dashed border-slate-200 text-center text-slate-400">
                    <p>Tidak ada cicilan atau kewajiban rutin yang dicatat. (Bagus jika bebas hutang!)</p>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {debts.map((debt) => (
                      <div
                        key={debt.id}
                        className="p-2.5 rounded-xl bg-white border border-slate-200 flex items-center justify-between gap-2 shadow-2xs"
                      >
                        <div>
                          <p className="font-bold text-slate-900 text-xs">{debt.name} (Cicilan)</p>
                          <p className="text-[10px] text-slate-400">
                            {formatIDR(debt.monthlyPayment)} / bulan
                            {debt.outstandingAmount > 0 && ` • Sisa pokok: ${formatCompactIDR(debt.outstandingAmount)}`}
                            {debt.remainingMonths && ` • ${debt.remainingMonths} bln lagi`}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeleteDebt(debt.id)}
                          disabled={isSubmitting}
                          className="p-1 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors cursor-pointer"
                          aria-label="Hapus cicilan"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}

                    {obligations.map((ob) => (
                      <div
                        key={ob.id}
                        className="p-2.5 rounded-xl bg-white border border-slate-200 flex items-center justify-between gap-2 shadow-2xs"
                      >
                        <div>
                          <p className="font-bold text-slate-900 text-xs">{ob.name} (Kewajiban)</p>
                          <p className="text-[10px] text-slate-400">
                            {formatIDR(ob.amount)} / {ob.frequency === 'monthly' ? 'bulan' : 'tahun'}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeleteObligation(ob.id)}
                          disabled={isSubmitting}
                          className="p-1 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors cursor-pointer"
                          aria-label="Hapus kewajiban"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 4: TABUNGAN & BEKAL AWAL */}
          {activeTab === 'saving' && (
            <div className="space-y-4">
              <div className="p-3 rounded-2xl bg-teal-50/70 border border-teal-100 flex items-start gap-2.5">
                <HelpCircle size={16} className="text-[#0EA5A4] flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-teal-900 leading-relaxed">
                  Catat tabungan atau saldo investasi yang sudah kamu siapkan saat ini. Tandai apakah dana ini <strong>tersedia untuk dialokasikan</strong> ke tujuan perencanaan ini.
                </p>
              </div>

              {/* Add Saving Form */}
              <form onSubmit={handleAddSaving} className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-3">
                <h4 className="font-bold text-slate-900 text-xs flex items-center gap-1.5">
                  <PiggyBank size={14} className="text-[#0EA5A4]" />
                  <span>Tambah Tabungan / Saldo Aset</span>
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                  <div className="sm:col-span-1">
                    <label className="block text-[10px] font-semibold text-slate-500 mb-1">
                      Nama Rekening / Aset
                    </label>
                    <input
                      type="text"
                      placeholder="Contoh: Tabungan Pendidikan"
                      value={newSavingName}
                      onChange={(e) => setNewSavingName(e.target.value)}
                      className="w-full px-3 py-1.5 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-1 focus:ring-teal-500 bg-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-500 mb-1">
                      Saldo Saat Ini (Rp)
                    </label>
                    <input
                      type="number"
                      placeholder="25000000"
                      value={newSavingBalance}
                      onChange={(e) => setNewSavingBalance(e.target.value)}
                      className="w-full px-3 py-1.5 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-1 focus:ring-teal-500 bg-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-500 mb-1">
                      Bisa Dipakai untuk Bekal?
                    </label>
                    <select
                      value={newSavingAvailable ? 'true' : 'false'}
                      onChange={(e) => setNewSavingAvailable(e.target.value === 'true')}
                      className="w-full px-2 py-1.5 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-1 focus:ring-teal-500 bg-white"
                    >
                      <option value="true">Ya, Tersedia untuk Bekal</option>
                      <option value="false">Tidak (Dana Darurat / Lainnya)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-500 mb-1">
                      Tujuan Pos
                    </label>
                    <div className="flex gap-1">
                      <select
                        value={newSavingPurpose}
                        onChange={(e) => setNewSavingPurpose(e.target.value)}
                        className="w-full px-2 py-1.5 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-1 focus:ring-teal-500 bg-white"
                      >
                        <option value="education">Pendidikan</option>
                        <option value="emergency">Dana Darurat</option>
                        <option value="general">Umum</option>
                      </select>
                      <button
                        type="submit"
                        disabled={isSubmitting || !newSavingName.trim() || !newSavingBalance}
                        className="px-3 py-1.5 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white font-bold text-xs shadow-2xs transition-all disabled:opacity-50 cursor-pointer flex-shrink-0"
                      >
                        Simpan
                      </button>
                    </div>
                  </div>
                </div>
              </form>

              {/* Savings List */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-slate-500 font-semibold text-[11px]">
                  <span>Daftar Tabungan ({savings.length})</span>
                  <span>Tersedia untuk Bekal: {formatIDR(liveSummary.availableSavings)}</span>
                </div>

                {savings.length === 0 ? (
                  <div className="p-4 rounded-2xl border border-dashed border-slate-200 text-center text-slate-400">
                    <p>Belum ada saldo tabungan yang dicatat.</p>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {savings.map((s) => (
                      <div
                        key={s.id}
                        className="p-2.5 rounded-xl bg-white border border-slate-200 flex items-center justify-between gap-2 shadow-2xs"
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                              s.isAvailableForGoal
                                ? 'bg-teal-100 text-teal-800'
                                : 'bg-slate-100 text-slate-500'
                            }`}
                          >
                            {s.isAvailableForGoal ? 'Tersedia' : 'Terkunci'}
                          </span>
                          <div>
                            <p className="font-bold text-slate-900 text-xs">{s.name}</p>
                            <p className="text-[10px] text-slate-400">
                              Saldo: {formatIDR(s.balance)}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeleteSaving(s.id)}
                          disabled={isSubmitting}
                          className="p-1 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors cursor-pointer"
                          aria-label="Hapus tabungan"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 5: RINGKASAN REALITA */}
          {activeTab === 'summary' && (
            <div className="space-y-4">
              <div className="p-4 rounded-2xl bg-gradient-to-br from-teal-50 via-white to-emerald-50/50 border border-teal-200 space-y-3">
                <span className="text-[10px] font-bold tracking-wider text-teal-800 uppercase">
                  Hasil Perhitungan Deterministik
                </span>
                <div className="flex items-baseline justify-between">
                  <div>
                    <h3 className="text-sm font-bold text-slate-800">
                      Ruang Kapasitas Bulanan Bersih (C_net)
                    </h3>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Pemasukan dikurangi seluruh pengeluaran dan komitmen berjalan
                    </p>
                  </div>
                  <div className="text-right">
                    <span
                      className={`text-lg sm:text-xl font-extrabold ${
                        liveSummary.netMonthlyCapacity >= 0 ? 'text-[#0F766E]' : 'text-amber-700'
                      }`}
                    >
                      {formatIDR(liveSummary.netMonthlyCapacity)}
                    </span>
                    <span className="block text-[10px] text-slate-400">/ bulan</span>
                  </div>
                </div>

                {liveSummary.netMonthlyCapacity < 0 && (
                  <div className="p-2.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-[11px] leading-relaxed">
                    Pengeluaran dan komitmen keluarga saat ini melampaui pemasukan bulanan sebesar{' '}
                    <strong>{formatIDR(Math.abs(liveSummary.netMonthlyCapacity))}</strong>. BekalNanti mencatat ini secara jujur untuk membantu merumuskan penyesuaian bekal yang realistis tanpa asumsi fiktif.
                  </div>
                )}
              </div>

              {/* Metric Breakdown Cards */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 space-y-1">
                  <span className="text-[10px] text-slate-500 font-semibold">Total Pemasukan</span>
                  <p className="font-bold text-slate-800 text-sm">
                    {formatIDR(liveSummary.totalMonthlyIncome)}
                  </p>
                  <p className="text-[10px] text-slate-400">/ bulan</p>
                </div>

                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 space-y-1">
                  <span className="text-[10px] text-slate-500 font-semibold">Total Pengeluaran</span>
                  <p className="font-bold text-slate-800 text-sm">
                    {formatIDR(liveSummary.totalMonthlyExpenses)}
                  </p>
                  <p className="text-[10px] text-slate-400">
                    Esensial: {formatCompactIDR(liveSummary.essentialMonthlyExpenses)}
                  </p>
                </div>

                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 space-y-1">
                  <span className="text-[10px] text-slate-500 font-semibold">Cicilan &amp; Komitmen</span>
                  <p className="font-bold text-slate-800 text-sm">
                    {formatIDR(liveSummary.totalMonthlyCommitments)}
                  </p>
                  <p className="text-[10px] text-slate-400">/ bulan</p>
                </div>

                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 space-y-1">
                  <span className="text-[10px] text-slate-500 font-semibold">Dana Awal Tersedia</span>
                  <p className="font-bold text-slate-800 text-sm">
                    {formatIDR(liveSummary.availableSavings)}
                  </p>
                  <p className="text-[10px] text-slate-400">
                    Total tabungan: {formatCompactIDR(liveSummary.totalSavings)}
                  </p>
                </div>
              </div>

              {/* Honest Notice */}
              <div className="p-3 rounded-xl bg-slate-100/70 border border-slate-200 text-slate-600 text-[11px] leading-relaxed">
                <strong>Catatan Privasi &amp; Kejujuran:</strong> BekalNanti bukan penasihat keuangan atau lembaga investasi. Seluruh angka di atas semata-mata adalah proyeksi matematis atas data yang kamu masukkan, tersimpan aman di akun keluarga Anda.
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-5 py-3.5 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between gap-2">
          {activeTab !== 'income' ? (
            <button
              type="button"
              onClick={() => {
                const idx = tabs.findIndex((t) => t.id === activeTab)
                const prev = tabs[idx - 1]
                if (prev) setActiveTab(prev.id as TabType)
              }}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-100 text-xs font-semibold cursor-pointer"
            >
              <ArrowLeft size={13} />
              <span>Sebelumnya</span>
            </button>
          ) : (
            <div />
          )}

          <div className="flex items-center gap-2">
            {activeTab !== 'summary' ? (
              <button
                type="button"
                onClick={() => {
                  const idx = tabs.findIndex((t) => t.id === activeTab)
                  const next = tabs[idx + 1]
                  if (next) setActiveTab(next.id as TabType)
                }}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white text-xs font-bold shadow-2xs cursor-pointer"
              >
                <span>Lanjut</span>
                <ArrowRight size={13} />
              </button>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white text-xs font-bold shadow-sm transition-all cursor-pointer"
              >
                <CheckCircle2 size={14} />
                <span>Simpan &amp; Kembali</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
